/* istanbul ignore file */
import { ErrorBoundary } from "react-error-boundary";
import { useAppComponents, useLogger } from "@amzn/xb-hooks";
import { XpsError } from "./federationUtils";
import { LiveContext } from "./LiveContext";
import { useContext } from "react";
import { StaticPagesContext } from "../interceptors/staticPageInterceptorFactory";

export const RemoteSideErrorBoundary = ({
  xpsName,
  componentName,
  children,
}: {
  xpsName: string;
  componentName: string;
  children: React.ReactNode;
}) => {
  const { log } = useLogger();
  const { Tier2LiveComponentErrorFallback } = useAppComponents();
  const { lcType, remoteProvidedErrorFallback } = useContext(LiveContext);
  const pages = useContext(StaticPagesContext);
  const E500Page = pages["E500"];

  // We have three different Fallback UI's which can be displayed.
  // 1. If it's a PageComponent, display the app-wide E500 page
  // 2. If it's a LiveComponent and doesn't provide a specific Fallback UI, display the app-wide LiveComponent fallback
  // 3. If it's a LiveComponent and provides it's own specific Fallback UI, display that (we need to turn it into a function b/c we've allowed people to pass ReactNodes)
  const getFallbackComponent = () => {
    if (lcType === "page") {
      const E500Component = () => <E500Page pageCode="E500" />;
      return E500Component;
    }

    if (!remoteProvidedErrorFallback) {
      return Tier2LiveComponentErrorFallback;
    }

    return typeof remoteProvidedErrorFallback === "function"
      ? remoteProvidedErrorFallback
      : () => remoteProvidedErrorFallback;
  };

  return (
    <ErrorBoundary
      FallbackComponent={getFallbackComponent()}
      onError={(error) => {
        log.error(`XPS "${xpsName}" Failed to Render "${componentName}."`, new XpsError({ error, name: "XPSError" }));
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
